import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addAdminApplicationsToEmailGroup,
  bulkUpdateAdminApplicationStatus,
  createAdminEmailGroupFromApplications,
  exportAdminApplicationsExcel,
  getAdminApplications,
  updateAdminApplicationStatus,
} from '../services/adminService';

const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const statusOptions = [
  { label: 'All statuses', value: '' },
  { label: 'New', value: 'new' },
  { label: 'Shortlisted', value: 'shortlisted' },
  { label: 'Group Interview', value: 'group-interview' },
  { label: 'Task Round', value: 'task-round' },
  { label: 'Final Interview', value: 'final-interview' },
  { label: 'Selected', value: 'selected' },
  { label: 'Rejected', value: 'rejected' },
];

const bulkStatuses = [
  { label: 'Mark for Group Interview', value: 'group-interview' },
  { label: 'Mark for Task Round', value: 'task-round' },
  { label: 'Mark for Final Interview', value: 'final-interview' },
  { label: 'Mark Selected', value: 'selected' },
  { label: 'Mark Rejected', value: 'rejected' },
];

const initialFilters = {
  search: '',
  status: '',
  job: '',
  groupId: '',
  dateFrom: '',
  dateTo: '',
  datePreset: '',
  showRejected: false,
  sort: 'newest',
  page: 1,
  limit: 50,
  dynamicFilters: {},
};

function formatDate(value) {
  if (!value) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatLabel(value) {
  return String(value || '')
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatAnswer(value) {
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : 'Not provided';
  }

  return value || 'Not provided';
}

function isRejectedStatus(status) {
  return String(status || '').trim().toLowerCase() === 'rejected';
}

function getStoredNameFromUrl(url = '') {
  const pathname = String(url || '').split('?')[0];
  return pathname.split('/').filter(Boolean).pop() || '';
}

function applicationFileUrl(file) {
  const storedName = file?.storedName || getStoredNameFromUrl(file?.url || file?.fileUrl);

  return storedName ? `${apiBaseUrl}/api/admin/applications/files/${encodeURIComponent(storedName)}` : '';
}

function getDownloadFilename(contentDisposition) {
  const fallback = 'factorstreet-applications-selected.xlsx';
  const match = String(contentDisposition || '').match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

function compactFilters(filters) {
  const params = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'dynamicFilters') return;
    if (value === false) return;
    if (value !== '' && value !== null && value !== undefined) {
      params[key] = value;
    }
  });

  const dynamicFilters = Object.fromEntries(
    Object.entries(filters.dynamicFilters || {}).filter(([, value]) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    )
  );

  if (Object.keys(dynamicFilters).length) {
    params.dynamicFilters = JSON.stringify(dynamicFilters);
  }

  return params;
}

function AdminApplications() {
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [applications, setApplications] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ jobs: [], groups: [], dynamicFields: [] });
  const [counts, setCounts] = useState({
    activeApplicants: 0,
    filteredCount: 0,
    page: 1,
    rejectedCount: 0,
    totalApplicants: 0,
    totalCount: 0,
    limit: 50,
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [loading, setLoading] = useState(true);

  const selectedVisibleCount = useMemo(
    () => applications.filter((application) => selectedIds.includes(application._id)).length,
    [applications, selectedIds]
  );
  const totalPages = Math.max(Math.ceil((counts.filteredCount || 0) / (counts.limit || 50)), 1);
  const allVisibleSelected = applications.length > 0 && applications.every((application) => selectedIds.includes(application._id));

  const loadApplications = useCallback(async () => {
    const data = await getAdminApplications(compactFilters(appliedFilters));
    setApplications(data.applications || []);
    setFilterOptions(data.filterOptions || { jobs: [], groups: [], dynamicFields: [] });
    setCounts({
      filteredCount: data.filteredCount || 0,
      page: data.page || 1,
      rejectedCount: data.rejectedCount || 0,
      activeApplicants: data.activeApplicants || 0,
      totalApplicants: data.totalApplicants || data.totalCount || 0,
      totalCount: data.totalCount || data.totalApplicants || 0,
      limit: data.limit || 50,
    });
    setSelectedIds((current) => current.filter((id) => (data.applications || []).some((application) => application._id === id)));
  }, [appliedFilters]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    loadApplications()
      .then(() => {
        if (isMounted) setLoading(false);
      })
      .catch(() => {
        if (isMounted) {
          setLoading(false);
          setFeedback({ type: 'error', message: 'Unable to load applications.' });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [loadApplications]);

  const updateFilter = (event) => {
    const { checked, name, type, value } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFilters((current) => ({
      ...current,
      [name]: nextValue,
      page: 1,
      ...(name === 'status' && value === 'rejected' ? { showRejected: true } : {}),
      ...(name === 'showRejected' && !checked && current.status === 'rejected' ? { status: '' } : {}),
      ...(name === 'dateFrom' || name === 'dateTo' ? { datePreset: '' } : {}),
    }));
  };

  const updateDynamicFilter = (fieldKey, value, checked = false, isCheckbox = false) => {
    setFilters((current) => {
      const currentValues = current.dynamicFilters[fieldKey] || [];
      const nextValue = isCheckbox
        ? checked
          ? Array.from(new Set([...currentValues, value]))
          : currentValues.filter((item) => item !== value)
        : value;

      return {
        ...current,
        page: 1,
        dynamicFilters: {
          ...current.dynamicFilters,
          [fieldKey]: nextValue,
        },
      };
    });
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setSelectedIds([]);
  };

  const refreshApplications = () => {
    setFeedback({ type: '', message: '' });
    loadApplications().catch(() => setFeedback({ type: 'error', message: 'Unable to refresh applications.' }));
  };

  const setDatePreset = (datePreset) => {
    setFilters((current) => ({ ...current, datePreset, dateFrom: '', dateTo: '', page: 1 }));
  };

  const goToPage = (page) => {
    const next = { ...appliedFilters, page };
    setFilters(next);
    setAppliedFilters(next);
  };

  const toggleSelected = (applicationId) => {
    setSelectedIds((current) =>
      current.includes(applicationId)
        ? current.filter((id) => id !== applicationId)
        : [...current, applicationId]
    );
  };

  const toggleDetails = (applicationId) => {
    setExpandedIds((current) =>
      current.includes(applicationId)
        ? current.filter((id) => id !== applicationId)
        : [...current, applicationId]
    );
  };

  const toggleVisibleSelection = () => {
    const visibleIds = applications.map((application) => application._id);
    setSelectedIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...current, ...visibleIds]))
    );
  };

  const runBulkStatus = async (status) => {
    if (selectedIds.length === 0) {
      setFeedback({ type: 'error', message: 'Select at least one application.' });
      return;
    }

    try {
      await bulkUpdateAdminApplicationStatus(selectedIds, status);
      await loadApplications();
      setFeedback({ type: 'success', message: `Updated ${selectedIds.length} application(s).` });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Unable to update status.' });
    }
  };

  const addToExistingGroup = async () => {
    if (!selectedGroupId || selectedIds.length === 0) {
      setFeedback({ type: 'error', message: 'Select candidates and choose a group.' });
      return;
    }

    try {
      const result = await addAdminApplicationsToEmailGroup(selectedGroupId, selectedIds);
      await loadApplications();
      setFeedback({
        type: 'success',
        message: `Added ${result.addedCount} emails to ${result.group?.slug || result.group?.name}. Skipped ${result.skippedExistingCount} already existing in this group. Skipped ${result.skippedInvalidCount} missing/invalid email.`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Unable to add candidates to group.' });
    }
  };

  const createGroupFromSelected = async (event) => {
    event.preventDefault();
    if (selectedIds.length === 0) {
      setFeedback({ type: 'error', message: 'Select at least one candidate.' });
      return;
    }

    try {
      const result = await createAdminEmailGroupFromApplications({
        ...newGroup,
        applicationIds: selectedIds,
      });
      await loadApplications();
      setSelectedGroupId(result.group?._id || '');
      setNewGroup({ name: '', description: '' });
      setShowCreateGroup(false);
      setFeedback({
        type: 'success',
        message: `Created ${result.group?.name}. Added ${result.addedCount} emails. Skipped ${result.skippedExistingCount} duplicates and ${result.skippedInvalidCount} invalid emails.`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Unable to create group.' });
    }
  };

  const changeApplicationStatus = async (application, status, notes = application.notes || '') => {
    const result = await updateAdminApplicationStatus(application._id, status, notes);
    const nextApplication = {
      ...result.application,
      emailGroups: application.emailGroups || [],
    };
    setApplications((current) =>
      current.map((item) => (item._id === application._id ? nextApplication : item))
    );
    if (status === 'rejected' && !appliedFilters.showRejected) {
      setSelectedIds((current) => current.filter((id) => id !== application._id));
      setExpandedIds((current) => current.filter((id) => id !== application._id));
      await loadApplications();
    }
  };

  const updateApplicationNotesDraft = (applicationId, notes) => {
    setApplications((current) =>
      current.map((application) =>
        application._id === applicationId ? { ...application, notes } : application
      )
    );
  };

  const downloadExcel = async () => {
    if (selectedIds.length === 0) {
      setFeedback({ type: 'error', message: 'Please select at least one candidate to download.' });
      return;
    }

    setDownloadingExcel(true);
    setFeedback({ type: '', message: '' });

    try {
      const response = await exportAdminApplicationsExcel(selectedIds);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getDownloadFilename(response.headers?.['content-disposition']);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setFeedback({ type: 'success', message: `Excel export prepared for ${selectedIds.length} candidate(s).` });
    } catch (error) {
      setFeedback({ type: 'error', message: 'Unable to download selected candidates.' });
    } finally {
      setDownloadingExcel(false);
    }
  };

  return (
    <section className="admin-page admin-applications-page">
      <div className="admin-page__header">
        <p className="eyebrow">Applications / Hiring operations</p>
        <h1>Submitted applications</h1>
        <p>Filter candidates, select applicants, and save shortlists as email groups for the email desk.</p>
      </div>

      {feedback.message ? (
        <p className={`form-alert form-alert--${feedback.type === 'error' ? 'error' : 'success'}`}>
          {feedback.message}
        </p>
      ) : null}

      <form className="admin-panel application-filter-panel" onSubmit={applyFilters}>
        <div className="admin-page__header--split application-count-bar">
          <div>
            <h2>
              {counts.filteredCount} / {appliedFilters.showRejected ? counts.totalApplicants : counts.activeApplicants} applicants
            </h2>
            <p className="application-count-bar__sub">
              {appliedFilters.showRejected
                ? `Rejected visible: ${counts.rejectedCount} / ${counts.totalApplicants}`
                : `Rejected: ${counts.rejectedCount} / ${counts.totalApplicants}`}
            </p>
            <span>{selectedIds.length} selected</span>
          </div>
          <div className="admin-bulk-actions">
            <button type="submit">Apply Filters</button>
            <button onClick={clearFilters} type="button">Clear Filters</button>
            <button onClick={refreshApplications} type="button">Refresh</button>
          </div>
        </div>

        <div className="admin-application-filter-grid">
          <label className="application-search-filter">
            Search
            <input
              name="search"
              onChange={updateFilter}
              placeholder="Search name, email, phone, job, or personal info"
              type="search"
              value={filters.search}
            />
          </label>
          <label>
            Status
            <select name="status" onChange={updateFilter} value={filters.status}>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Job / Position
            <select name="job" onChange={updateFilter} value={filters.job}>
              <option value="">All jobs</option>
              {filterOptions.jobs?.map((job) => (
                <option key={job.slug || job.title} value={job.slug || job.title}>
                  {job.title || job.slug} ({job.count})
                </option>
              ))}
            </select>
          </label>
          <label>
            Group
            <select name="groupId" onChange={updateFilter} value={filters.groupId}>
              <option value="">All groups</option>
              <option value="none">Not in any group</option>
              {filterOptions.groups?.map((group) => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.count})
                </option>
              ))}
            </select>
          </label>
          <label>
            From date
            <input name="dateFrom" onChange={updateFilter} type="date" value={filters.dateFrom} />
          </label>
          <label>
            To date
            <input name="dateTo" onChange={updateFilter} type="date" value={filters.dateTo} />
          </label>
          <label>
            Sort
            <select name="sort" onChange={updateFilter} value={filters.sort}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name">Name A-Z</option>
            </select>
          </label>
          <label className="application-show-rejected">
            <input
              checked={filters.showRejected}
              name="showRejected"
              onChange={updateFilter}
              type="checkbox"
            />
            Show rejected candidates
          </label>
        </div>

        <div className="application-date-actions">
          <button className={filters.datePreset === 'today' ? 'is-active' : ''} onClick={() => setDatePreset('today')} type="button">
            Today
          </button>
          <button className={filters.datePreset === 'last-7-days' ? 'is-active' : ''} onClick={() => setDatePreset('last-7-days')} type="button">
            Last 7 days
          </button>
          <button className={filters.datePreset === 'last-30-days' ? 'is-active' : ''} onClick={() => setDatePreset('last-30-days')} type="button">
            Last 30 days
          </button>
          <button onClick={() => setFilters((current) => ({ ...current, datePreset: '', dateFrom: '', dateTo: '', page: 1 }))} type="button">
            Clear date filter
          </button>
        </div>

        <div className="application-active-chips">
          {appliedFilters.showRejected ? <span className="application-danger-chip">Rejected candidates are visible</span> : null}
          {Object.entries(compactFilters(appliedFilters))
            .filter(([key]) => !['page', 'limit', 'dynamicFilters', 'showRejected'].includes(key))
            .map(([key, value]) => (
              <span key={key}>{formatLabel(key)}: {value}</span>
            ))}
        </div>

        <button
          className="application-advanced-toggle"
          onClick={() => setShowAdvanced((current) => !current)}
          type="button"
        >
          {showAdvanced ? 'Hide advanced filters' : 'Show advanced filters'}
        </button>

        {showAdvanced ? (
          <div className="application-advanced-filters">
            {filterOptions.dynamicFields?.map((field) => (
              <div className="application-dynamic-filter" key={field.key}>
                <strong>{field.label}</strong>
                <span>{field.categoryTitle}</span>
                {field.type === 'checkbox' ? (
                  <div className="application-checkbox-filter">
                    {(field.options || []).map((option) => (
                      <label key={option}>
                        <input
                          checked={(filters.dynamicFilters[field.key] || []).includes(option)}
                          onChange={(event) => updateDynamicFilter(field.key, option, event.target.checked, true)}
                          type="checkbox"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                ) : (
                  <select
                    onChange={(event) => updateDynamicFilter(field.key, event.target.value)}
                    value={filters.dynamicFilters[field.key] || ''}
                  >
                    <option value="">Any</option>
                    {(field.options || []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
            {!filterOptions.dynamicFields?.length ? (
              <p className="admin-empty">No dropdown, radio, or checkbox fields are available in Apply CMS.</p>
            ) : null}
          </div>
        ) : null}
      </form>

      <section className="admin-panel application-group-actions">
        <div className="admin-page__header--split">
          <div>
            <h2>Group selected candidates</h2>
            <span>{selectedIds.length} selected, {selectedVisibleCount} visible on this page</span>
          </div>
          <button className="btn btn--secondary" onClick={() => setShowCreateGroup((current) => !current)} type="button">
            Create new group from selected
          </button>
        </div>
        <div className="application-group-action-row">
          <select onChange={(event) => setSelectedGroupId(event.target.value)} value={selectedGroupId}>
            <option value="">Select existing group</option>
            {filterOptions.groups?.map((group) => (
              <option key={group._id} value={group._id}>
                {group.name} ({group.count})
              </option>
            ))}
          </select>
          <button className="btn btn--primary" onClick={addToExistingGroup} type="button">
            Add selected to group
          </button>
        </div>
        {showCreateGroup ? (
          <form className="application-create-group-form" onSubmit={createGroupFromSelected}>
            <label>
              Group name
              <input
                onChange={(event) => setNewGroup((current) => ({ ...current, name: event.target.value }))}
                placeholder="group-interview-27-may"
                value={newGroup.name}
              />
            </label>
            <label>
              Description
              <input
                onChange={(event) => setNewGroup((current) => ({ ...current, description: event.target.value }))}
                placeholder="Optional note for the email desk"
                value={newGroup.description}
              />
            </label>
            <button className="btn btn--primary" disabled={!newGroup.name.trim()} type="submit">
              Create group
            </button>
          </form>
        ) : null}
      </section>

      <div className="admin-panel admin-applications-list-panel">
        <div className="admin-page__header--split">
          <div>
            <h2>Submitted Applications</h2>
            <span>{loading ? 'Loading candidates...' : `${applications.length} shown on this page`}</span>
            <span className="application-selection-help">Select all selects visible candidates on this page only.</span>
          </div>
          <div className="admin-bulk-actions">
            <button onClick={toggleVisibleSelection} type="button">
              {allVisibleSelected ? 'Clear page selection' : 'Select all'}
            </button>
            {bulkStatuses.map((action) => (
              <button key={action.value} onClick={() => runBulkStatus(action.value)} type="button">
                {action.label}
              </button>
            ))}
            <button disabled={downloadingExcel} onClick={downloadExcel} type="button">
              {downloadingExcel ? 'Downloading...' : 'Download Excel'}
            </button>
          </div>
        </div>

        <div className="application-card-list">
          {applications.map((application) => (
            <article
              className={`application-card-shell ${selectedIds.includes(application._id) ? 'is-selected' : ''} ${
                isRejectedStatus(application.status) ? 'is-rejected' : ''
              }`}
              key={application._id}
            >
              <div className="application-card">
                <label className="application-card__check">
                  <input
                    checked={selectedIds.includes(application._id)}
                    onChange={() => toggleSelected(application._id)}
                    type="checkbox"
                  />
                </label>
                <div className="application-card__main">
                  <strong>{application.candidate?.name || 'Unnamed candidate'}</strong>
                  <span>{application.candidate?.email || 'No email'}</span>
                  <span>{application.candidate?.phone || 'No phone'}</span>
                </div>
                <div className="application-card__meta">
                  <span>{application.jobTitle || 'Open application'}</span>
                  <span>
                    Status:{' '}
                    {isRejectedStatus(application.status) ? (
                      <strong className="application-status-badge application-status-badge--rejected">Rejected</strong>
                    ) : (
                      formatLabel(application.status)
                    )}
                  </span>
                  <span>Files: {application.uploadedFiles?.length || 0}</span>
                  <span>{formatDate(application.createdAt)}</span>
                </div>
                <div className="application-card__groups">
                  {application.emailGroups?.length ? (
                    application.emailGroups.slice(0, 3).map((group) => <span key={group._id}>{group.name}</span>)
                  ) : (
                    <span>Not in any group</span>
                  )}
                </div>
                <button className="btn btn--secondary" onClick={() => toggleDetails(application._id)} type="button">
                  {expandedIds.includes(application._id) ? 'Hide details' : 'View details'}
                </button>
              </div>

              {expandedIds.includes(application._id) ? (
                <div className="application-inline-detail">
                  <div className="lead-detail-grid">
                    <span>Email</span>
                    <strong>{application.candidate?.email || 'Not provided'}</strong>
                    <span>Phone</span>
                    <strong>{application.candidate?.phone || 'Not provided'}</strong>
                    <span>Job</span>
                    <strong>{application.jobTitle || 'Open application'}</strong>
                    <span>Status</span>
                    <select
                      onChange={(event) => changeApplicationStatus(application, event.target.value)}
                      value={application.status}
                    >
                      {statusOptions
                        .filter((option) => option.value)
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                    <span>Submitted</span>
                    <strong>{formatDate(application.createdAt)}</strong>
                    <span>Groups</span>
                    <strong>
                      {application.emailGroups?.length
                        ? application.emailGroups.map((group) => group.name).join(', ')
                        : 'Not in any group'}
                    </strong>
                  </div>

                  <label className="application-notes">
                    Admin notes
                    <textarea
                      onBlur={(event) => changeApplicationStatus(application, application.status, event.target.value)}
                      onChange={(event) => updateApplicationNotesDraft(application._id, event.target.value)}
                      rows="4"
                      value={application.notes || ''}
                    />
                  </label>

                  <div className="application-response-groups">
                    {Object.entries(
                      (application.responses || []).reduce((groups, response) => {
                        const key = response.categoryTitle || 'Other';
                        groups[key] = groups[key] || [];
                        groups[key].push(response);
                        return groups;
                      }, {})
                    ).map(([categoryTitle, responses]) => (
                      <section className="application-response-group" key={categoryTitle}>
                        <h3>{categoryTitle}</h3>
                        {responses.map((response) => (
                          <div className="application-response-row" key={`${response.fieldKey}-${response.fieldLabel}`}>
                            <span>{response.fieldLabel}</span>
                            {response.fileUrl ? (
                              <a
                                download={response.originalFileName || true}
                                href={applicationFileUrl(response)}
                                rel="noreferrer"
                                target="_blank"
                              >
                                {response.originalFileName || 'Download file'}
                              </a>
                            ) : (
                              <strong>{formatAnswer(response.value)}</strong>
                            )}
                          </div>
                        ))}
                      </section>
                    ))}
                  </div>

                  {application.uploadedFiles?.length ? (
                    <div className="application-files">
                      <h3>Uploaded files</h3>
                      {application.uploadedFiles.map((file) => (
                        <a
                          download={file.originalName || true}
                          href={applicationFileUrl(file)}
                          key={file.storedName}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {file.fieldLabel}: {file.originalName}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </article>
          ))}
          {loading ? <p className="admin-empty">Loading applications...</p> : null}
          {!loading && applications.length === 0 ? <p className="admin-empty">No applications found.</p> : null}
        </div>

        <div className="application-pagination">
          <button disabled={counts.page <= 1} onClick={() => goToPage(counts.page - 1)} type="button">
            Previous
          </button>
          <span>Page {counts.page} of {totalPages}</span>
          <button disabled={counts.page >= totalPages} onClick={() => goToPage(counts.page + 1)} type="button">
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export default AdminApplications;
